import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { palletCodeKey } from './palletEditor';

// "Add existing pallets by code": one code or several ("03012, 03013"), typed or pasted, in the packing list form and the label form. What was
// found goes on the form; what was not is said in words and stays in the box to be corrected. On a packing list a pallet that is on another
// list is refused; on a label it is welcome, because a label can be printed again at any time. In a label form (`forForm`) a pallet that is in
// ANOTHER label form is refused: a pallet is in one form at most. The server decides, here and again at Save.
export default function PalletCodeAdder({ forList = false, forForm = false, formId = null, branchId, listId = null, heldCodes, onAdd, touch, T }) {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);     // { added, problems: [text] }

  const submit = async () => {
    const typed = text.trim();
    if (!typed || busy) return;
    setBusy(true); setNote(null);
    try {
      const res = await authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/lookup`,
        params: { branchId, code: typed, ...(listId ? { listId } : {}) },
      });
      const held = new Set((heldCodes || []).map(palletCodeKey).filter(Boolean));
      const good = [], missing = [], here = [], problems = [], keep = [];
      for (const row of (res.data && res.data.rows) || []) {
        if (held.has(row.key)) { here.push(row.input); continue; }
        const otherForm = forForm && row.pallet && row.pallet.labelFormId && String(row.pallet.labelFormId) !== String(formId || '') && row.pallet.labelForm;
        if (otherForm) { keep.push(row.input); problems.push(t('mis.lfPalletInOtherForm', { code: row.pallet.reference, number: row.pallet.labelForm.docNumber })); continue; }
        if (row.status === 'found' || (row.status === 'on_list' && !forList)) { good.push(row.pallet); held.add(row.key); continue; }
        keep.push(row.input);
        if (row.status === 'on_list') problems.push(t('mis.lblOnOtherList', { code: row.pallet.reference, list: row.list.docNumber }));
        else if (row.status === 'other_branch') problems.push(t('mis.lblOtherBranch', { code: row.input, branch: row.branchName }));
        else missing.push(row.input);
      }
      if (missing.length) problems.unshift(t('mis.lblNotFound', { codes: missing.join(', ') }));
      if (here.length) problems.push(t('mis.lblAlreadyHere', { codes: here.join(', ') }));
      if (good.length) onAdd(good);
      setText(keep.join(', '));
      setNote({ added: good.length, problems });
    } catch (_) {
      setNote({ added: 0, problems: [t('mis.lblLookupFailed')] });
    } finally {
      setBusy(false);
    }
  };

  // A column pasted from a spreadsheet: a single-line box would run its lines together.
  const onPaste = (e) => {
    const pasted = e.clipboardData && e.clipboardData.getData('text');
    if (!pasted || !/[\r\n\t]/.test(pasted)) return;
    e.preventDefault();
    setText((cur) => [cur.trim().replace(/,$/, ''), pasted.trim().replace(/[\r\n\t]+/g, ', ')].filter(Boolean).join(', '));
  };

  return (
    <Box data-testid="pl-adder" sx={{ mt: 1.5, p: 1.25, borderRadius: '10px', border: `1px dashed ${T.BD2 || T.BD}` }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: touch ? 'column' : 'row' }}>
        <TextField fullWidth size="small" label={t('mis.lblAddByCode')} value={text} InputLabelProps={{ shrink: true }}
          placeholder={t('mis.lblAddByCodePlaceholder')} helperText={t('mis.lblAddByCodeHelp')}
          onChange={(e) => { setText(e.target.value); setNote(null); }} onPaste={onPaste}
          onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) { e.preventDefault(); submit(); } }}
          inputProps={{ dir: 'ltr', enterKeyHint: 'done', 'data-testid': 'pl-adder-input' }} />
        <Button variant="outlined" size="small" onClick={submit} disabled={!text.trim() || busy} data-testid="pl-adder-add"
          startIcon={busy ? <CircularProgress size={13} color="inherit" /> : <PlaylistAddIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', flexShrink: 0, height: 40, ...(touch ? { width: '100%', height: 48 } : {}) }}>
          {t('mis.lblAddButton')}
        </Button>
      </Box>
      {note && (
        <Box data-testid="pl-adder-note" role="status" sx={{ mt: 0.75 }}>
          {note.added > 0 && (
            <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>{t('mis.lblAddedNote', { count: note.added })}</Typography>
          )}
          {note.problems.map((line) => (
            <Typography key={line} sx={{ fontSize: '0.7rem', color: 'error.main' }}>{line}</Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
